import React, { useMemo } from 'react';

/**
 * Phase 5: Scraper pipeline telemetry panel.
 *
 * Composed of:
 *   - HealthChip: overall "healthy / warning / critical" indicator derived
 *     from queue waiting counts + CycleTLS pool utilization (Critical >5k / Busy >2.5k waiting).
 *   - PipelineFunnel: scrape -> country -> activity -> stored funnel with
 *     percent-passed bars.
 *   - SourcingFunnel: fetched -> qualified -> grok -> added funnel.
 *   - UsernameMiniBars: per-username country+activity progress.
 *   - ThroughputEstimator: rolling throughput (users/min) based on the last
 *     two health snapshots (client-side delta, so no extra backend cost).
 *
 * The panel degrades gracefully when no health payload has arrived yet or
 * when per-session data is empty (e.g. session filters out country+activity).
 */

function classNames(...xs) {
  return xs.filter(Boolean).join(' ');
}

function formatNumber(n) {
  if (n == null) return '—';
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(1)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}k`;
  return `${x}`;
}

function percent(numerator, denominator) {
  if (!denominator || denominator <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((numerator / denominator) * 100)));
}

function HealthChip({ health }) {
  const { label, colorClass } = useMemo(() => {
    if (!health) {
      return { label: 'Waiting…', colorClass: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200' };
    }
    const cycle = health.cycleTls || {};
    const qCountry = health.queues?.countryCheck || {};
    const qActivity = health.queues?.activityCheck || {};
    const waitCountry = qCountry.waiting || 0;
    const waitActivity = qActivity.waiting || 0;
    const util = cycle.utilization || 0;
    // Critical: either queue > 5k waiting OR CycleTLS >95% utilization
    if (waitCountry > 5000 || waitActivity > 5000 || util > 95) {
      return { label: 'Critical', colorClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' };
    }
    // Warning: either queue > 2.5k waiting OR CycleTLS >80%
    if (waitCountry > 2500 || waitActivity > 2500 || util > 80) {
      return { label: 'Busy', colorClass: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' };
    }
    return { label: 'Healthy', colorClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' };
  }, [health]);

  return (
    <span className={classNames('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium', colorClass)}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {label}
    </span>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{value}</span>
      {sub != null && (
        <span className="text-[11px] text-slate-500 dark:text-slate-400">{sub}</span>
      )}
    </div>
  );
}

function FunnelBar({ label, passed, total, color = 'purple' }) {
  const pct = percent(passed, total);
  const colorClass = {
    purple: 'bg-purple-500 dark:bg-purple-500',
    emerald: 'bg-emerald-500 dark:bg-emerald-500',
    sky: 'bg-sky-500 dark:bg-sky-500',
    amber: 'bg-amber-500 dark:bg-amber-500',
    rose: 'bg-rose-500 dark:bg-rose-500',
  }[color] || 'bg-purple-500';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600 dark:text-slate-300">{label}</span>
        <span className="text-slate-500 dark:text-slate-400">
          {formatNumber(passed)} / {formatNumber(total)} <span className="text-slate-400">({pct}%)</span>
        </span>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
        <div
          className={classNames('h-full rounded-full transition-all duration-500', colorClass)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function PipelineFunnel({ session }) {
  if (!session) return null;
  const f = session.funnel || {};
  const countryOn = !!session.filters?.filterCountryWhitelist;
  const activityOn = !!session.filters?.filterActiveLastMonth;

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Check pipeline</h4>
        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          {countryOn && 'Country'}{countryOn && activityOn ? ' + ' : ''}{activityOn && 'Activity'}
          {!countryOn && !activityOn && 'Store all'}
        </span>
      </div>
      {countryOn && (
        <FunnelBar
          label="Country accepted"
          passed={f.totalCountryAccepted || 0}
          total={f.totalCountryChecked || 0}
          color="sky"
        />
      )}
      {activityOn && (
        <FunnelBar
          label="Active last month"
          passed={f.totalActive || 0}
          total={f.totalActivityChecked || 0}
          color="emerald"
        />
      )}
      <FunnelBar
        label="Stored (vs scraped)"
        passed={f.totalStored || 0}
        total={f.totalScraped || 0}
        color="purple"
      />
      <div className="grid grid-cols-4 gap-2 pt-1">
        <MiniStat label="Scraped" value={formatNumber(f.totalScraped)} />
        <MiniStat label="Stored" value={formatNumber(f.totalStored)} />
        <MiniStat label="Skipped" value={formatNumber(f.totalSkipped)} />
        <MiniStat label="Dupes" value={formatNumber(f.totalDuplicates)} />
      </div>
    </div>
  );
}

function SourcingFunnel({ session }) {
  if (!session?.filters?.sourcingEnabled) return null;
  const s = session.sourcing || {};
  const fetched = s.totalFetched || 0;
  const qualified = s.totalQualified || 0;
  const added = s.totalAdded || 0;
  const grokFiltered = s.totalGrokFiltered || 0;

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Sourcing</h4>
        {s.lastSourceAccount && (
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            via @{s.lastSourceAccount}
          </span>
        )}
      </div>
      <FunnelBar label="Qualified vs fetched" passed={qualified} total={fetched} color="amber" />
      <FunnelBar label="Added vs qualified" passed={added} total={qualified} color="emerald" />
      <div className="grid grid-cols-4 gap-2 pt-1">
        <MiniStat label="Fetched" value={formatNumber(fetched)} />
        <MiniStat label="Qualified" value={formatNumber(qualified)} />
        <MiniStat label="Grok skip" value={formatNumber(grokFiltered)} />
        <MiniStat label="Added" value={formatNumber(added)} />
      </div>
    </div>
  );
}

function ThroughputEstimator({ history }) {
  const throughput = useMemo(() => {
    if (!history || history.length < 2) return null;
    const latest = history[history.length - 1];
    const previous = history[history.length - 2];
    if (!latest || !previous) return null;
    const dtMs = Math.max(1, latest.ts - previous.ts);
    const deltaCountry = Math.max(0, (latest.totalCountryChecked || 0) - (previous.totalCountryChecked || 0));
    const deltaActivity = Math.max(0, (latest.totalActivityChecked || 0) - (previous.totalActivityChecked || 0));
    const deltaScraped = Math.max(0, (latest.totalScraped || 0) - (previous.totalScraped || 0));
    const perMin = (x) => Math.round((x * 60000) / dtMs);
    return {
      scraped: perMin(deltaScraped),
      country: perMin(deltaCountry),
      activity: perMin(deltaActivity),
    };
  }, [history]);

  if (!throughput) {
    return (
      <div className="text-[11px] text-slate-500 dark:text-slate-400">
        Throughput: measuring…
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-3 text-[11px]">
      <span className="text-slate-600 dark:text-slate-300">
        Scrape: <span className="font-semibold">{formatNumber(throughput.scraped)}</span>/min
      </span>
      <span className="text-slate-600 dark:text-slate-300">
        Country: <span className="font-semibold">{formatNumber(throughput.country)}</span>/min
      </span>
      <span className="text-slate-600 dark:text-slate-300">
        Activity: <span className="font-semibold">{formatNumber(throughput.activity)}</span>/min
      </span>
    </div>
  );
}

function shortReplicaLabel(s) {
  // Defensive client-side shortener: Railway REPLICA_ID / INSTANCE_ID is a
  // 36-char UUID which blows out the card width. Take first 8 hex chars.
  const str = String(s || '');
  const parts = str.split(':');
  const tail = parts[parts.length - 1] || str;
  const noDash = tail.replace(/-/g, '');
  return noDash.slice(0, 8) || str.slice(0, 8) || '—';
}

function ReplicaClaimsPanel({ replicaClaims = {} }) {
  // Sort replicas by label for stable "Replica 1 / 2 / 3" numbering
  // across renders.
  const replicas = useMemo(() => {
    const list = Object.values(replicaClaims || {});
    list.sort((a, b) => String(a.instanceId).localeCompare(String(b.instanceId)));
    return list;
  }, [replicaClaims]);

  const totals = useMemo(
    () =>
      replicas.reduce(
        (acc, r) => {
          acc.country += r.country || 0;
          acc.activity += r.activity || 0;
          acc.accounts += r.accounts || 0;
          return acc;
        },
        { country: 0, activity: 0, accounts: 0 }
      ),
    [replicas]
  );

  if (replicas.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 px-3 py-2 text-[11px] text-slate-500 dark:text-slate-400">
        Checker replicas: waiting for first heartbeat…
      </div>
    );
  }

  const now = Date.now();

  // Under the store-first (Phase B) pipeline the backend emits an explicit
  // `accounts` field — one shared pool per replica — plus per-stage
  // processed counters. The old Bull-based pipeline only emitted
  // country/activity CLAIM counts with no separate accounts field. Detect
  // which shape we're getting and label accordingly, so Replica 1 shows
  // "Accounts" (pool size) and "Checked country / activity" (live work)
  // instead of two confusing zeros.
  const storeFirstShape = replicas.some((r) => Number.isFinite(r.accounts));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {storeFirstShape ? 'Checker pool & throughput' : 'Checker account claims'}
          </h4>
          <span className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {replicas.length} replica{replicas.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          {storeFirstShape && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
              Pool {formatNumber(totals.accounts)}
            </span>
          )}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">
            <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
            {storeFirstShape ? 'Country checked' : 'Country'} {formatNumber(totals.country)}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {storeFirstShape ? 'Activity checked' : 'Activity'} {formatNumber(totals.activity)}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {replicas.map((r, idx) => {
          const ageMs = Math.max(0, now - (r.ts || 0));
          const stale = ageMs > 9000; // 3× emit interval
          const label = shortReplicaLabel(r.replicaLabel || r.instanceId);
          const country = r.country || 0;
          const activity = r.activity || 0;
          const accounts = r.accounts || 0;
          const headerRight = storeFirstShape
            ? `${formatNumber(accounts)} accts`
            : `${formatNumber(country + activity)} total`;
          return (
            <div
              key={r.instanceId}
              className={classNames(
                'rounded-lg border px-3 py-2',
                stale
                  ? 'border-amber-300/40 bg-amber-50/40 dark:border-amber-500/30 dark:bg-amber-500/10'
                  : 'border-slate-200 bg-slate-50/60 dark:border-slate-700 dark:bg-slate-900/40'
              )}
              title={`instanceId=${r.instanceId} · last tick ${Math.round(ageMs / 1000)}s ago`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className={classNames(
                      'h-2 w-2 rounded-full flex-shrink-0',
                      stale ? 'bg-amber-500' : 'bg-emerald-500'
                    )}
                  />
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Replica {idx + 1}
                  </span>
                  <span className="font-mono text-[10px] text-slate-500 dark:text-slate-400 truncate">
                    {label}
                  </span>
                </div>
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  {headerRight}
                </span>
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <div className="flex items-center justify-between rounded-md bg-sky-50 dark:bg-sky-900/20 px-2 py-1">
                  <span className="text-[10px] uppercase tracking-wide text-sky-700 dark:text-sky-300">
                    {storeFirstShape ? 'Country ✓' : 'Country'}
                  </span>
                  <span className="text-sm font-semibold text-sky-800 dark:text-sky-200 tabular-nums">
                    {formatNumber(country)}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-md bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1">
                  <span className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                    {storeFirstShape ? 'Activity ✓' : 'Activity'}
                  </span>
                  <span className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 tabular-nums">
                    {formatNumber(activity)}
                  </span>
                </div>
              </div>
              {stale && (
                <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-400">
                  No heartbeat for {Math.round(ageMs / 1000)}s
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * ScraperPipelinePanel
 * @param {object} props
 * @param {object} props.health latest scraper-pipeline-health payload
 * @param {Array<object>} props.history rolling history of session funnels for throughput estimation
 * @param {object} props.replicaClaims instanceId → { country, activity, replicaLabel, ts }
 *                 map merged client-side across heartbeats from all Railway replicas
 * @param {string|null} props.focusedSessionId optional: show only this session's funnel
 */
export default function ScraperPipelinePanel({
  health,
  history = [],
  replicaClaims = {},
  focusedSessionId = null,
}) {
  const sessions = useMemo(() => {
    if (!health?.sessions) return [];
    if (focusedSessionId) return health.sessions.filter(s => s.sessionId === focusedSessionId);
    return health.sessions;
  }, [health, focusedSessionId]);

  const queues = health?.queues || {};
  const cycle = health?.cycleTls || {};

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Pipeline Health</h3>
          <HealthChip health={health} />
        </div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400">
          {health?.ts ? new Date(health.ts).toLocaleTimeString() : 'Waiting for first update…'}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat
          label="Scraping queue"
          value={formatNumber(queues.scraping?.waiting)}
          sub={`${queues.scraping?.active || 0} active`}
        />
        <MiniStat
          label="Country queue"
          value={formatNumber(queues.countryCheck?.waiting)}
          sub={`${queues.countryCheck?.active || 0} active`}
        />
        <MiniStat
          label="Activity queue"
          value={formatNumber(queues.activityCheck?.waiting)}
          sub={`${queues.activityCheck?.active || 0} active`}
        />
        <MiniStat
          label="CycleTLS pool"
          value={`${cycle.inUse || 0} / ${cycle.cap || 0}`}
          sub={`${cycle.utilization || 0}% used`}
        />
      </div>

      <ReplicaClaimsPanel replicaClaims={replicaClaims} />

      {sessions.length === 0 ? (
        <div className="text-center py-4 text-sm text-slate-500 dark:text-slate-400">
          No active scraper sessions.
        </div>
      ) : (
        <div className="space-y-4">
          {sessions.map((s) => (
            <div
              key={s.sessionId}
              className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 p-3 space-y-3"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    Session {s.sessionId}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">
                    {s.usernames?.completed || 0}/{s.usernames?.total || 0} usernames · {s.status}
                  </div>
                </div>
                <ThroughputEstimator
                  history={history.filter(h => h.sessionId === s.sessionId)}
                />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <PipelineFunnel session={s} />
                <SourcingFunnel session={s} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
